'use client';

import { useReporting } from '@/hooks/useReporting';
import useRouteInfo from '@/hooks/useRouteInfo';
import { ProviderType } from '@/reporting';
import logger from '@/utils/client-logger';
import { getCurrentScrollPercentage } from '@/utils/scroll';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useRef } from 'react';

export default function PageTracking() {
  const { trackEvent } = useReporting();
  const { getFromRouterInfo } = useRouteInfo();
  const pathname = usePathname();
  const enterTimer = useRef<number>(Date.now());
  // 记录最后的滚动状态，避免页面跳转时滚动位置被重置
  const lastScrollState = useRef({ viewAdvance: 0 });

  // 页面离开事件追踪
  const trackPageLeave = useCallback(() => {
    try {
      const viewDuration = Math.ceil((Date.now() - enterTimer.current) / 1000);
      const { viewAdvance } = lastScrollState.current;

      trackEvent(
        {
          eventName: 'InviteFriendPage_Leave',
          properties: {
            view_advance: viewAdvance,
            view_duration: viewDuration,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('InviteFriendPage_Leave tracking error:', error);
    }
  }, [trackEvent]);

  // 更新滚动状态
  const updateScrollState = useCallback(() => {
    try {
      const viewAdvance = getCurrentScrollPercentage();
      lastScrollState.current = { viewAdvance };
    } catch (error) {
      logger.error('Error updating scroll state:', error);
    }
  }, []);

  // 页面浏览事件追踪
  useEffect(() => {
    enterTimer.current = Date.now();
    const { refer_page_name, refer_url_path } = getFromRouterInfo();
    trackEvent(
      {
        eventName: 'InviteFriendPage_View',
        properties: {
          refer_page_name: refer_page_name || '',
          refer_url_path: refer_url_path || '',
        },
      },
      [ProviderType.SENSORS],
    );
  }, []);

  // 监听滚动事件，更新滚动状态
  useEffect(() => {
    // 立即更新一次
    updateScrollState();

    // 监听滚动事件
    const handleScroll = () => {
      updateScrollState();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [updateScrollState]);

  // 监听路由变化，触发离开事件
  useEffect(() => {
    return () => {
      trackPageLeave();
    };
  }, [pathname, trackPageLeave]);

  return null;
}
